/**

 * Usage:
 *   const viewer = new DraggableViewer({
 *     zone : $('.zone'),    // jQuery object — clipping container
 *     img  : $('.image'),   // jQuery object — the oversized image
 *   });
 *
 *   viewer.reset();    // re-centre
 *   viewer.destroy();  // clean up
 */

import {Draggable, gsap, InertiaPlugin} from "gsap/all";
import $ from "jquery";

export class DraggableViewer {
  constructor({zone, images, initialKey = null, overshoot = 0.55, bounceDur = 0.65, resistance = 260, fadeDur = 0.25, maxOverDrag = 100} = {}) {
    gsap.registerPlugin(Draggable, InertiaPlugin);

    this.zone = $(zone);
    this.images = $(images); // All the possible images
    this.overshoot = overshoot;
    this.bounceDur = bounceDur;
    this.resistance = resistance;
    this.fadeDur = fadeDur;
    this.maxOverDrag = maxOverDrag;
    this.draggable = null;
    this.activeImg = null;

    this.init(initialKey);
  }

  // ── Bootstrap ────────────────────────────────────────────────────
  init(initialKey) {
    // Determine starting image — first key provided, else first in collection
    const firstKey = initialKey ?? this.images.first().data('choice-key');
    this.showImmediate(firstKey);

    // Delay draggable init until layout is ready
    requestAnimationFrame(() => {
      this.initDraggable();
    });
  }

  // ── Find image by data-choice-key ─────────────────────────────────
  findImage(key) {
    return this.images.filter(`[data-choice-key="${key}"]`);
  }

  // ── Bounds (based on active image) ───────────────────────────────
  getBounds() {
    const el = this.activeImg;
    return {
      minX: this.zone.outerWidth() - el.outerWidth(),
      maxX: 0,
      minY: this.zone.outerHeight() - el.outerHeight(),
      maxY: 0,
    };
  }

  // ── Center (based on active image) ───────────────────────────────
  getCenter() {
    const b = this.getBounds();
    return {
      x: b.minX / 2,
      y: b.minY / 2,
    };
  }

  // ── Elastic snap-back ─────────────────────────────────────────────
  snapBack(x, y) {
    const {minX, maxX, minY, maxY} = this.getBounds();
    const clampedX = Math.min(maxX, Math.max(minX, x));
    const clampedY = Math.min(maxY, Math.max(minY, y));

    if (clampedX !== x || clampedY !== y) {
      gsap.to(this.activeImg[0], {
        x: clampedX,
        y: clampedY,
        duration: this.bounceDur,
        ease: 'power3.out',
        overwrite: true,
      });
    }
  }

  // ── Clamp single axis (InertiaPlugin end callback) ────────────────
  clamp(axis, val) {
    const b = this.getBounds();
    return Math.min(
      axis === 'x' ? b.maxX : b.maxY,
      Math.max(axis === 'x' ? b.minX : b.minY, val)
    );
  }

  // ── Swap the Draggable target to a new image element ──────────────
  retarget(imgEl) {
    if (this.draggable) this.draggable.kill();
    this.initDraggable(imgEl);
  }

  // ── GSAP Draggable setup ──────────────────────────────────────────
  initDraggable(target) {
    const el = target ?? this.activeImg[0];

    const b = this.getBounds();
    const bounds = {
      minX: b.minX - this.maxOverDrag,
      maxX: b.maxX + this.maxOverDrag,
      minY: b.minY - this.maxOverDrag,
      maxY: b.maxY + this.maxOverDrag,
    };

    this.draggable = Draggable.create(el, {
      type: 'x,y',
      bounds,

      inertia: true,
      edgeResistance: 0.75,
      allowNativeTouchScrolling: false,

      onPress: () => {
        this.zone.css('cursor', 'grabbing');
        gsap.killTweensOf(this.activeImg[0]);
      },

      onRelease: () => {
        this.zone.css('cursor', 'grab');
      },

      onDragEnd: () => {
        this.snapBack(
          gsap.getProperty(this.activeImg[0], 'x'),
          gsap.getProperty(this.activeImg[0], 'y')
        );
      },
    })[0];

    // Track x/y so InertiaPlugin can measure velocity if needed
    InertiaPlugin.track(el, 'x,y');
  }

  // ── Show immediately (no transition) — used on init ───────────────
  showImmediate(key) {
    const target = this.findImage(key);
    if (!target.length) {
      return;
    }
    this.images.removeClass("active");
    this.activeImg = target;
    this.activeImg.addClass("active");

    const {x, y} = this.getCenter();
    gsap.set(this.activeImg[0], {opacity: 1, x, y});
  }

  // ── Public: cross-fade to image by key ───────────────────────────
  show(key) {
    const target = this.findImage(key);
    if (!target.length) {
      return;
    }
    if (target.is(this.activeImg)) return; // already active

    const outgoing = this.activeImg;
    const incoming = target;

    // Kill any running tweens / inertia on both images
    gsap.killTweensOf(outgoing[0]);
    gsap.killTweensOf(incoming[0]);

    // Preserve current position from outgoing image
    const currentX = gsap.getProperty(outgoing[0], 'x');
    const currentY = gsap.getProperty(outgoing[0], 'y');

    // Temporarily switch activeImg to compute bounds for incoming
    const prevActive = this.activeImg;
    this.activeImg = incoming;

    // Clamp position to new image bounds
    const clampedX = this.clamp('x', currentX);
    const clampedY = this.clamp('y', currentY);

    // Apply preserved (clamped) position
    gsap.set(incoming[0], {x: clampedX, y: clampedY, opacity: 0});

    // Restore (will be set properly below anyway)
    this.activeImg = prevActive;

    // Ensure incoming is visible per CSS
    incoming.addClass("active");

    // Cross-fade layering
    gsap.set(incoming[0], {zIndex: 2});
    gsap.set(outgoing[0], {zIndex: 1});

    // Fade OUT (old map)
    gsap.to(outgoing[0], {
      opacity: 0,
      duration: this.fadeDur * 0.25,
      ease: 'none',
      delay: this.fadeDur
    });

    // Fade IN (new map) — slight overlap only
    gsap.to(incoming[0], {
      opacity: 1,
      duration: this.fadeDur,
      ease: 'none',
    });

    // Cleanup (aligned with actual end)
    gsap.delayedCall(this.fadeDur * 1.2, () => {
      outgoing.removeClass("active");
      gsap.set(outgoing[0], { zIndex: 0 });
      gsap.set(incoming[0], { zIndex: 1 });
    });

    // Swap active reference and re-target Draggable
    this.activeImg = incoming;
    this.retarget(incoming[0]);
  }

  // ── Public: re-centre current image ──────────────────────────────
  reset(duration = 0.6) {
    const {x, y} = this.getCenter();
    gsap.to(this.activeImg[0], {x, y, duration, ease: 'power3.out', overwrite: true});
  }

  // ── Public: tear down ─────────────────────────────────────────────
  destroy() {
    if (this.draggable) this.draggable.kill();
    this.images.each((i, el) => gsap.killTweensOf(el));
    this.draggable = null;
  }
}
